import {
  queryOptions,
  skipToken,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
  type QueryClient,
  type UseSuspenseQueryOptions,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { hydraUiApi } from '@/services/api';
import type { Action } from '../action/entity';
import { getActionQueryOptions } from '../action/queries';
import type { QueryConfig } from '../shared/react-query-types';
import { useAllCached, type UseAllCachedOptions } from '../shared/use-all-cached';
import { useEntitiesByIds } from '../shared/use-entities-by-ids';
import { requireWorkspaceId } from '../shared/workspace';
import type { Tag } from '../tag/entity';
import { getTagQueryOptions } from '../tag/queries';
import {
  parseCampaignModel,
  parseCampaignsListResponse,
  populateCampaignModel,
  populateCampaignsList,
  type ParsedCampaignsListResponse,
} from './populate';
import { campaignKeys, type Campaign, type CampaignListParams } from './entity';

/**
 * Campaign read queries.
 *
 * Each query is exposed as:
 *
 *   1. A plain async fetcher (`getCampaigns(...)`, `getCampaign(...)`) —
 *      callable from anywhere, used by routers / loaders / non-React code.
 *   2. A `getXQueryOptions` factory returning a `queryOptions(...)` config
 *      — composable with `useQuery`, `useSuspenseQuery`, `prefetchQuery`,
 *      `useQueries`, etc.
 *   3. A thin hook wrapper (`useCampaignsList`, `useCampaign`) that calls
 *      `useSuspenseQuery` with the options + caller-side `queryConfig`.
 *
 * Callers compose at whichever layer fits the use case.
 */

const STALE_TIME = 60_000;

/* ------------------------------------------------------------------------- */
/* getCampaigns — paginated list with cluster sideload                        */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCampaigns(
  workspaceId: number,
  params: CampaignListParams,
  qc: QueryClient,
): Promise<ParsedCampaignsListResponse> {
  const raw = await hydraUiApi.campaigns.list({
    params: {
      path: { environment_id: String(workspaceId) },
      query: {
        page: params.page - 1, // 1-based at hook boundary, 0-based for backend
        state: params.state,
        type: params.type,
        'searchType[]': params.searchTypes,
        subscriptionTopicId: params.subscriptionTopicId,
        sendToUnsubscribed: params.sendToUnsubscribed,
        sort: params.sort,
        asc: params.asc,
        version: '2',
        excludeMetrics: true,
        excludeSegmentMatchCount: true,
      },
    },
  });
  const response = parseCampaignsListResponse(raw);
  // Read-side cluster propagation — fans detail caches for every entity in
  // the cluster (under the workspace's scope prefix), populates per-campaign
  // relation slots. Runs synchronously inside the queryFn so by-id and
  // relation hooks see the data on the same render pass.
  populateCampaignsList(qc, workspaceId, response);
  return response;
}

export const getCampaignsQueryOptions = (workspaceId: number, params: CampaignListParams, qc: QueryClient) =>
  queryOptions({
    queryKey: campaignKeys.list(workspaceId, params),
    queryFn: () => getCampaigns(workspaceId, params, qc),
    staleTime: STALE_TIME,
  });

interface UseCampaignsListOptions {
  params: CampaignListParams;
  queryConfig?: QueryConfig<typeof getCampaignsQueryOptions>;
}

/**
 * Throws synchronously during render if no workspace id is in the URL —
 * see `requireWorkspaceId`. For layouts/banners that may mount before
 * the URL settles, build a parallel `useXOptional` hook over `useQuery`
 * with `skipToken` (see `useDomainsOptional` for the pattern).
 */
/** @public */
export const useCampaignsList = ({ params, queryConfig }: UseCampaignsListOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getCampaignsQueryOptions(workspaceId, params, qc),
    ...queryConfig,
  });
};

/* ------------------------------------------------------------------------- */
/* getCampaign — by-id with cluster sideload                                  */
/* ------------------------------------------------------------------------- */

/** @public */
export async function getCampaign(workspaceId: number, campaignId: string, qc: QueryClient) {
  const raw = await hydraUiApi.campaigns.get({
    params: { path: { environment_id: String(workspaceId), campaign_id: campaignId } },
  });
  const parsed = parseCampaignModel(raw);
  populateCampaignModel(qc, workspaceId, parsed);
  return parsed.campaign;
}

/** @public */
export const getCampaignQueryOptions = (workspaceId: number, campaignId: string, qc: QueryClient) =>
  queryOptions({
    queryKey: campaignKeys.detail(workspaceId, campaignId),
    queryFn: () => getCampaign(workspaceId, campaignId, qc),
    staleTime: STALE_TIME,
  });

interface UseCampaignOptions {
  id: string;
  queryConfig?: QueryConfig<typeof getCampaignQueryOptions>;
}

/** @public */
export const useCampaign = ({ id, queryConfig }: UseCampaignOptions) => {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  return useSuspenseQuery({
    ...getCampaignQueryOptions(workspaceId, id, qc),
    ...queryConfig,
  });
};

interface UseCampaignOptionalOptions {
  workspaceId: number;
  id: string;
  enabled?: boolean;
}

/**
 * Non-suspense, conditionally-enabled by-id hook. For callers resolving a
 * campaign outside a suspense boundary that may keep the query dormant.
 * `skipToken` (not `enabled: false`) so no cache slot is registered while
 * inactive; `retry: false` so a 404 surfaces immediately for deleted-resource
 * detection.
 *
 * @public
 */
export const useCampaignOptional = ({ workspaceId, id, enabled = true }: UseCampaignOptionalOptions) => {
  const qc = useQueryClient();
  const active = enabled && workspaceId > 0 && id !== '';
  return useQuery({
    queryKey: active ? campaignKeys.detail(workspaceId, id) : ['campaign', 'inactive'],
    queryFn: active ? () => getCampaign(workspaceId, id, qc) : skipToken,
    staleTime: STALE_TIME,
    retry: false,
  });
};

/* ------------------------------------------------------------------------- */
/* Relation reads — follow FK arrays to entity detail caches                  */
/* ------------------------------------------------------------------------- */

/**
 * Resolve a campaign's `workflow_action_ids` to the cached Action entities.
 *
 * Normalized read: walks `campaign.workflow_action_ids` and follows each
 * id into `actionKeys.detail(wsId, id)`. Each Action lives in exactly one
 * cache slot — its detail. This relation view is derived, not stored.
 *
 * If any referenced action isn't cached, its by-id queryFn fires
 * (suspending). For our cluster sideload pattern, all referenced actions
 * arrived together, so cache hits are the common case.
 */
/** @public */
export function useCampaignActions(campaign: Campaign): readonly Action[] {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const optionsForId = useCallback(
    (id: string) => getActionQueryOptions(workspaceId, id, qc) as UseSuspenseQueryOptions<Action>,
    [workspaceId, qc],
  );
  return useEntitiesByIds<Action>(optionsForId, campaign.workflow_action_ids);
}

/**
 * Belongs-to FK follow: read the campaign an action references via its
 * `campaign_id`. Lives here (not in `action/queries.ts`) to avoid a
 * circular import — the hook returns a `Campaign`, so the campaign
 * module is the honest home.
 *
 * **Non-suspending.** Returns `data: undefined` when the action has no
 * `campaign_id` (orphaned / scratch actions); otherwise resolves from
 * cache (the action's parent list ran first) or fires the by-id
 * queryFn for deep links. Callers must handle `data: undefined` plus
 * the usual `isPending` / `isError` states.
 *
 * Mirrors `useDomainsOptional`'s shape: `useQuery` with `skipToken` for
 * the missing-input case so an absent `campaign_id` doesn't register a
 * sentinel slot in the workspace cache namespace. Throwing during
 * render (the previous shape) made the hook unsafe for components
 * rendered before `campaign_id` settled and risked hooks-rules
 * violations if the field flipped between renders.
 *
 *   const { data: campaign } = useCampaignForAction(action);
 *   if (campaign == null) return null; // no campaign_id, or still loading
 */
/** @public */
export function useCampaignForAction(actionInstance: Action) {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const campaignId = actionInstance.campaign_id;
  return useQuery({
    // Sentinel key sits outside the `['workspace', wsId, ...]` namespace
    // so it survives `clearWorkspaceQueries`. `skipToken` guarantees no
    // data ever lands here. Don't `setQueryData` against this key.
    queryKey: campaignId != null ? campaignKeys.detail(workspaceId, campaignId) : ['campaign', 'no-campaign-id'],
    queryFn: campaignId != null ? () => getCampaign(workspaceId, campaignId, qc) : skipToken,
    staleTime: STALE_TIME,
  });
}

/**
 * Resolve a campaign's `tag_ids` to the cached Tag entities. Same FK-follow
 * pattern as `useCampaignActions`.
 */
/** @public */
export function useCampaignTags(campaign: Campaign): readonly Tag[] {
  const workspaceId = requireWorkspaceId();
  const qc = useQueryClient();
  const optionsForId = useCallback(
    (id: string) => getTagQueryOptions(workspaceId, id, qc) as UseSuspenseQueryOptions<Tag>,
    [workspaceId, qc],
  );
  return useEntitiesByIds<Tag>(optionsForId, campaign.tag_ids);
}

/**
 * Campaigns the cache has seen that reference this tag id.
 *
 * Inverse of campaign↔tag, derived from the cached campaign details
 * rather than a dedicated server view. Lives here (not in
 * `tag/queries.ts`) to break the campaign/tag circular import — it's a
 * campaign read parameterized by tag id.
 *
 * **Not authoritative** — only includes campaigns currently sideloaded
 * (typically from the campaigns list pages the user has visited). With
 * a real "campaigns for tag" backend, replace with a dedicated suspense
 * query.
 */
/**
 * **Re-renders on every cache event under `campaign.details`.** The
 * `select` callback is memoized on `tagId`, so the filter only re-runs
 * when the underlying snapshot changes. Consumers doing further
 * derivation downstream should still wrap their own work in `useMemo`
 * if it's expensive.
 *
 * **Cardinality.** Sized for the same budget as `useAllCached` itself —
 * up to a few hundred cached campaigns. Workspaces routinely caching
 * thousands of campaigns should grow a dedicated paginated
 * `getCampaignsForTag(tagId, page)` query rather than scaling this
 * derived view.
 */
/** @public */
export function useCampaignsForTag(tagId: string): readonly Campaign[] {
  const select = useCallback(
    (campaigns: readonly Campaign[]) => campaigns.filter((c) => c.tag_ids.includes(tagId)),
    [tagId],
  );
  return useCampaigns({ select });
}

/* ------------------------------------------------------------------------- */
/* useCampaigns — store-wide read of every cached campaign                    */
/* ------------------------------------------------------------------------- */

/**
 * Reactive read of every campaign currently in the cache, across all
 * detail slots, regardless of which list page or detail fetch loaded
 * them. **No fetch fires** — purely a cache subscription.
 *
 * Use to derive views without coupling to a specific list query:
 *
 *   // All campaigns currently cached:
 *   const all = useCampaigns();
 *
 *   // Campaigns referencing a tag (the `useCampaignsForTag` use case
 *   // we previously had as a dedicated inverse cache slot):
 *   const tagged = useCampaigns({
 *     select: (cs) => cs.filter((c) => c.tag_ids.includes(tagId)),
 *   });
 *
 * Re-renders when any campaign detail slot is added, updated, or removed.
 * Returns a stable array reference when nothing has changed (so render
 * skipping works in consumers). `select` is a derivation hook — wrap in
 * `useMemo` if your filter creates non-stable references and you care.
 *
 * Compared to a list query: this is bound to the entity table, not to
 * any one query. Whatever's been sideloaded — by list, by detail, by
 * mutation response — shows up here. Pages getting GC'd don't lose data
 * from the entity table; this hook follows.
 */
/** @public */
export function useCampaignsForWorkspace<TSelected = readonly Campaign[]>(
  workspaceId: number,
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected {
  return useAllCached<Campaign, TSelected>(campaignKeys.details(workspaceId), options);
}

/** @public */
export function useCampaigns<TSelected = readonly Campaign[]>(
  options?: UseAllCachedOptions<Campaign, TSelected>,
): TSelected {
  const workspaceId = requireWorkspaceId();
  return useCampaignsForWorkspace(workspaceId, options);
}
